import './AreaChart.scss';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, AreaChart as AC, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import {
  CompanyFiscalCal,
  dateStringToUtcTimestamp,
  toBpmDateString
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { getColorMap, Neutral200, Neutral500 } from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { CARTESIAN_GRID_STROKE, CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '../ChartConstants';
import { ChartTooltipV2, TooltipPassthroughProps } from '../ChartTooltip/ChartTooltipV2';
import { headerFormatter, useDynamicYAxisWidth } from '../ChartUtils';
import { DefaultLegend } from '../Legend/DefaultLegend/DefaultLegend';
import { LegendWithGrouping } from '../Legend/LegendWithGrouping';
import { useBuildMonthlyXAxis } from '../MonthlyXAxis/MonthlyXAxis';

const INACTIVE_COLOR = Neutral200;

interface AreaChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: any[];
  dateGrouping?: string;
  fiscalCal: CompanyFiscalCal;
  xAxisDataKey: string;
  tooltipFormatter?: (value: any) => string;
  tooltipProps?: TooltipPassthroughProps;
}

const AreaChartTooltip = ({
  active,
  payload,
  dateGrouping,
  fiscalCal,
  xAxisDataKey,
  tooltipFormatter,
  tooltipProps = {}
}: AreaChartTooltipProps) => {
  if (!active || !payload || !payload.length || !dateGrouping) return null;

  const value = payload[0].payload[xAxisDataKey];
  const header = headerFormatter(dateGrouping, value, fiscalCal);
  const items = R.sortBy(
    (item: any) => -Math.abs(item.value),
    payload.map(item => ({
      label: item.name as string,
      value: tooltipFormatter ? tooltipFormatter(item.value) : (item.value as number),
      color: item.stroke
    }))
  );

  return (
    <ChartTooltipV2
      {...tooltipProps}
      itemKeyShape={tooltipProps.itemKeyShape ?? 'square'}
      headers={[header]}
      items={items}
    />
  );
};

interface AreaItem {
  name: string;
  dataKey: string;
  grouping?: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  incrementality?: boolean;
  toolTip?: string;
  toolTipColor?: string;
}
export type Areas = AreaItem[];

interface AreaChartProps {
  areas: Areas;
  data: any[];
  dateGrouping?: string;
  colorOverrides?: Record<string, string>;
  hardCodedColor?: string;
  hardCodedColorName?: string;
  hideLegend?: boolean;
  groupedAreas?: any[];
  incrementality?: boolean;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  reverseLegend?: boolean;
  usePercentageYAxis?: boolean;
  // Clamp the y-axis to a non-negative range so it never renders values below 0. Off by default
  // (existing callers keep the auto domain that can dip negative).
  nonNegativeYAxis?: boolean;
  xAxisDataKey: string;
  xAxisTickFormatter?: (value: any) => string;
  yAxisDataKey?: string;
  yAxisTickFormatter?: (value: any) => string;
  yAxisStaticWidth?: number;
  setChartYAxisDynamicWidth?: (number) => void;
  legendTitle?: string;
  canUseMonthlyTicks?: boolean;
  tooltipProps?: TooltipPassthroughProps;
  xAxisDomain?: [number, number];
  // Explicit x-axis tick timestamps. When provided, they override recharts' auto-generated
  // ticks so this chart can share an identical tick set with another stacked chart.
  xAxisTicks?: number[];
  chartMargin?: { top?: number; right?: number; left?: number; bottom?: number };
}

export const AreaChart = ({
  areas,
  data,
  dateGrouping,
  colorOverrides,
  hardCodedColor,
  hardCodedColorName,
  hideLegend,
  groupedAreas,
  incrementality,
  syncId,
  tooltipFormatter,
  reverseLegend,
  usePercentageYAxis = false,
  nonNegativeYAxis = false,
  xAxisDataKey,
  xAxisTickFormatter,
  yAxisDataKey,
  yAxisTickFormatter,
  yAxisStaticWidth,
  setChartYAxisDynamicWidth,
  legendTitle,
  canUseMonthlyTicks = false,
  tooltipProps = {},
  xAxisDomain,
  xAxisTicks,
  chartMargin
}: AreaChartProps) => {
  const [activeArea, setActiveArea] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const { xAxisHeight, yAxisWidth, xAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data,
    yAxisStaticWidth
  });

  useEffect(() => {
    if (setChartYAxisDynamicWidth) {
      setChartYAxisDynamicWidth(yAxisWidth);
    }
  }, [setChartYAxisDynamicWidth, yAxisWidth]);

  const colorMap = useMemo(() => {
    if (R.isEmpty(areas)) return undefined;
    const colorsOriginal = getColorMap(areas, hardCodedColorName, hardCodedColor);
    return { ...colorsOriginal, ...colorOverrides };
  }, [areas, hardCodedColorName, hardCodedColor, colorOverrides]);

  const handleLegendClick = useCallback(
    (line: string, groupChannel?: string) => {
      if (activeArea === line) {
        setActiveArea(null);
        setActiveGroup(null);
      } else {
        setActiveArea(line);
        if (groupChannel) {
          setActiveGroup(groupChannel);
        }
      }
    },
    [activeArea]
  );

  const handleLegendEnter = useCallback(
    line => {
      if (hoveredLegend === line) {
        setHoveredLegend(null);
      } else {
        setHoveredLegend(line);
      }
    },
    [hoveredLegend]
  );

  const buildAreas = useCallback(
    () =>
      areas.map((area, i) => {
        const isActiveArea = activeArea === null ? true : activeArea === area.name;
        const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
        if (!isActiveArea && activeArea !== null) {
          return null;
        }

        const activeColor = isActiveArea && colorMap ? colorMap[area.name] : INACTIVE_COLOR;
        const hoverColor = isHoveredLegend && colorMap ? colorMap[area.name] : INACTIVE_COLOR;

        return (
          <Area
            activeDot={false}
            dataKey={area.dataKey}
            fill={activeArea ? activeColor : hoverColor}
            fillOpacity={1}
            isAnimationActive={false}
            key={i}
            name={area.name}
            stackId='1'
            stroke={activeArea ? activeColor : hoverColor}
            strokeWidth={0}
            type='monotone'
          />
        );
      }),
    [colorMap, areas, activeArea, hoveredLegend]
  );

  const percentageYAxis = (
    <YAxis
      dataKey={yAxisDataKey}
      domain={[0, 100]}
      orientation='left'
      tick={TICK_STYLE}
      ticks={[0, 25, 50, 75, 100]}
      type='number'
      yAxisId='0'
      unit='%'
    />
  );

  const dataWithTimestamps = useMemo(() => {
    if (xAxisDataKey !== 'date') {
      return data;
    }

    return data.map(row => ({
      ...row,
      timestamp: dateStringToUtcTimestamp(toBpmDateString(row?.date))
    }));
  }, [data, xAxisDataKey]);

  const defaultXAxis = useMemo(() => {
    if (xAxisDataKey === 'date' && xAxisDomain) {
      return (
        <XAxis
          dataKey='timestamp'
          type='number'
          scale='time'
          domain={[xAxisDomain[0], xAxisDomain[1]]}
          ticks={xAxisTicks}
          allowDataOverflow
          axisLine={false}
          height={xAxisHeight}
          tick={TICK_STYLE}
          tickFormatter={val => {
            // Use UTC getters: the series timestamps are UTC-midnight (dateStringToUtcTimestamp),
            // and a numeric time axis places ticks at arbitrary times of day. Local getters would
            // land on a different calendar day than a UTC-based chart on the same domain (e.g.
            // ActualVsPredicted, which uses UTC), producing mismatched x-axis labels between two
            // charts that share a domain.
            const date = new Date(val);
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            return xAxisTickFormatter ? xAxisTickFormatter(dateStr) : dateStr;
          }}
        />
      );
    }
    return (
      <XAxis
        axisLine={false}
        dataKey={xAxisDataKey}
        height={xAxisHeight}
        tick={TICK_STYLE}
        tickFormatter={xAxisTickFormatter}
      />
    );
  }, [xAxisDataKey, xAxisHeight, xAxisTickFormatter, xAxisDomain, xAxisTicks]);

  const { shouldRenderFallback: shouldRenderFallbackAxis, component: monthlyXAxis } =
    useBuildMonthlyXAxis({
      data: dataWithTimestamps,
      yAxisWidth: yAxisStaticWidth || yAxisWidth,
      xAxisHeight,
      axisWidth: xAxisWidth,
      domain: xAxisDomain,
      overrideTicks: xAxisTicks
    });
  const shouldRenderMonthlyTimestamps = canUseMonthlyTicks && !shouldRenderFallbackAxis;

  const xAxisComponent = useMemo(
    () => (shouldRenderMonthlyTimestamps ? monthlyXAxis : defaultXAxis),
    [defaultXAxis, monthlyXAxis, shouldRenderMonthlyTimestamps]
  );

  return (
    <div className='area-chart'>
      <div className='chart-sizer'>
        <AutoSizer
          renderProp={({ width, height }) => (
            <AC
              data={dataWithTimestamps}
              height={height ?? 0}
              syncId={syncId}
              ref={setChartRef}
              width={width ?? 0}
              margin={chartMargin}
            >
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              {xAxisComponent}
              {usePercentageYAxis && percentageYAxis}
              {!usePercentageYAxis && (
                <YAxis
                  axisLine={false}
                  dataKey={yAxisDataKey}
                  domain={nonNegativeYAxis ? [0, 'auto'] : undefined}
                  allowDataOverflow={nonNegativeYAxis}
                  tick={TICK_STYLE}
                  tickFormatter={yAxisTickFormatter}
                  tickLine={false}
                  width={yAxisStaticWidth || yAxisWidth}
                />
              )}
              {buildAreas()}
              <Tooltip
                content={
                  <AreaChartTooltip
                    dateGrouping={dateGrouping}
                    fiscalCal={fiscalCal}
                    xAxisDataKey={xAxisDataKey}
                    tooltipFormatter={tooltipFormatter}
                    tooltipProps={tooltipProps}
                  />
                }
                cursor={{ stroke: Neutral500, width: '1px' }}
                isAnimationActive={false}
              />
            </AC>
          )}
        />
      </div>
      <div className={`right-of-chart ${hideLegend ? 'hide' : ''}`}>
        <div className='legend-title'>{legendTitle}</div>
        {(!groupedAreas || R.isEmpty(groupedAreas)) && (
          <DefaultLegend
            areas={areas}
            activeArea={activeArea}
            handleLegendClick={handleLegendClick}
            incrementality={incrementality}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            colorMap={colorMap}
            reverseLegend={reverseLegend}
          />
        )}
        {groupedAreas && !R.isEmpty(groupedAreas) && (
          <LegendWithGrouping
            activeArea={activeArea}
            activeGroup={activeGroup}
            handleLegendClick={handleLegendClick}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            incrementality={incrementality}
            colorMap={colorMap}
            groupedAreas={groupedAreas}
          />
        )}
      </div>
    </div>
  );
};
